(ns app.domain.licence.model
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.licence.generic-model :as gl-model]
            [app.domain.workspace.model :as workspace-model]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [schema.core :as s]))

(defn alcohol-licence-application-form-schema-id [group-id]
  (str group-id "/alcoholic-beverage-licence-application"))

(defn alcohol-licence-renewal-form-schema-id [group-id]
  (str group-id "/alcoholic-beverage-licence-application---renewal"))

(defn business-licence-application-form-schema-id [group-id]
  (str group-id "/business-licence-application"))

(defn business-licence-renewal-form-schema-id [group-id]
  (str group-id "/business-licence-application---renewal"))

(defn cannabis-licence-application-form-schema-id [group-id]
  (str group-id "/cannabis-establishment-licence-application"))

(defn cannabis-licence-renewal-form-schema-id [group-id]
  (str group-id "/cannabis-establishment-licence-application---renewal"))

(defn fireworks-display-form-schema-id [group-id]
  (str group-id "/fireworks-display-permit-application"))

(defn fireworks-sales-form-schema-id [group-id]
  (str group-id "/fireworks-sales-permit-application"))

(defn gaming-licence-form-schema-id [group-id]
  (str group-id "/gaming-licence"))

(defn home-occupation-application-form-schema-id [group-id]
  (str group-id "/home-occupation-application"))

(defn home-occupation-renewal-form-schema-id [group-id]
  (str group-id "/home-occupation-application---renewal"))

(defn mobile-food-vendor-application-form-schema-id [group-id]
  (str group-id "/mobile-food-vendor-application"))

(defn mobile-food-vendor-renewal-form-schema-id [group-id]
  (str group-id "/mobile-food-vendor-application---renewal"))

(defn occupational-tax-certificate-form-schema-id [group-id]
  (str group-id "/occupational-tax-certificates"))

(defn pawn-shop-form-schema-id [group-id]
  (str group-id "/pawn-shop---second-hand-dealer-application"))

(defn stvr-licence-application-form-schema-id [group-id]
  (str group-id "/short-term-vacation-rental--stvr--licence-application"))

(defn stvr-licence-renewal-form-schema-id [group-id]
  (str group-id "/short-term-vacation-rental--stvr--licence-application---renewal"))

(defn special-event-form-schema-id [group-id]
  (str group-id "/special-event-permit-application"))

(defn taxi-licence-application-form-schema-id [group-id]
  (str group-id "/taxi-application"))

(defn vendor-licence-application-form-schema-id [group-id]
  (str group-id "/vendor-licence-application"))

(defn vendor-licence-renewal-form-schema-id [group-id]
  (str group-id "/vendor-licence-application---renewal"))

(def ^:private work-type-form-id-fns
  {:category/alcohol-licence              {:work-type/new     alcohol-licence-application-form-schema-id
                                           :work-type/renewal alcohol-licence-renewal-form-schema-id}
   :category/business-licence             {:work-type/new     business-licence-application-form-schema-id
                                           :work-type/renewal business-licence-renewal-form-schema-id}
   :category/cannabis                     {:work-type/new     cannabis-licence-application-form-schema-id
                                           :work-type/renewal cannabis-licence-renewal-form-schema-id}
   :category/gaming-licence               {:work-type/new gaming-licence-form-schema-id}
   :category/home-occupation              {:work-type/new     home-occupation-application-form-schema-id
                                           :work-type/renewal home-occupation-renewal-form-schema-id}
   :category/mobile-food-vendor           {:work-type/new     mobile-food-vendor-application-form-schema-id
                                           :work-type/renewal mobile-food-vendor-renewal-form-schema-id}
   :category/occupational-tax-certificate {:work-type/new occupational-tax-certificate-form-schema-id}
   :category/pawn-shop                    {:work-type/new pawn-shop-form-schema-id}
   :category/short-term-vacation-rental   {:work-type/new     stvr-licence-application-form-schema-id
                                           :work-type/renewal stvr-licence-renewal-form-schema-id}
   :category/special-event                {:work-type/new special-event-form-schema-id}
   :category/taxi-licence                 {:work-type/new taxi-licence-application-form-schema-id}
   :category/vendor-licence               {:work-type/new     vendor-licence-application-form-schema-id
                                           :work-type/renewal vendor-licence-renewal-form-schema-id}})

(def ^:private work-target-form-id-fns
  {:category/fireworks {:work-type/new {:work-target/fireworks-sales   fireworks-sales-form-schema-id
                                        :work-target/fireworks-display fireworks-display-form-schema-id}}})

(defn work-type-form-schemas
  [group-id category work-type]
  (when-let [id-fn (get-in work-type-form-id-fns [category work-type])]
    #{(id-fn group-id)}))

(defn work-target-form-schemas
  [group-id category work-type work-target]
  (when-let [id-fn (get-in work-target-form-id-fns [category work-type work-target])]
    #{(id-fn group-id)}))

(def dashboard-filter-condition-attributes
  [:domain/state
   :group/id
   :action-items
   :expiration-date
   :expiration-date-range
   :issue-date-range
   :category-names])

(def date-range-filter-attributes #{:expiration-date-range :issue-date-range})

(def visible-licence-states #{:licence.state/active
                              :licence.state/expired
                              :licence.state/revoked})


(defn role->licence-role
  "Which licence role should be given to which licence application party role?"
  [role]
  (case role
    :role/applicant :role.licence/writer
    :role.licence/reader))

(def search-sort-attributes
  [:modified
   :issue-date
   :licence/valid-to])

(s/defschema LicenceSearchSorting
  (s/pair (apply s/enum search-sort-attributes) "Sort attribute"
          (s/enum :asc :desc) "Sort direction"))

(defn global-id
  "Make :licence/global-id"
  [group-id licence-id]
  (str group-id "-" licence-id))

(def show-notification-controls-for-level?
  ;; see also app.domain.workspace-notification.model/supported-levels
  #{:group-config.level/permit-type
    :group-config.level/category})

(def notification-types
  [:workspace-notification.type/licence-expiration-first-warning
   :workspace-notification.type/licence-expiration-second-warning
   :workspace-notification.type/licence-expiration-final-warning])

(def read-only-type?
  #{:workspace-notification.type/licence-expiration-final-warning})

(def default-notification-configs
  [{:workspace-notification-config/type     :workspace-notification.type/licence-expiration-first-warning
    :workspace-notification-config/interval 60}
   {:workspace-notification-config/type     :workspace-notification.type/licence-expiration-second-warning
    :workspace-notification-config/interval 30}
   {:workspace-notification-config/type     :workspace-notification.type/licence-expiration-final-warning
    :workspace-notification-config/interval 1}])

(defn valid-interval?
  [interval]
  (<= 1 interval 180))

(defn category-name
  "Get the name of the licence category in the given language(s)"
  [licence lang-or-langs]
  ((i18n-model/translation-fallback-fn :group.config/names) (:licence/category-group-config licence) lang-or-langs))

(def revocation-letter-template-variables
  (concat ["licence-id"
           "licence-category"]
          gl-model/revocation-letter-common-template-variables
          gl-model/holder-revocation-letter-template-variables))

(defn revocation-letter-filename [licence]
  (str (:licence/id licence)
       "-revocation-letter.pdf"))

(defn renewal-group-config? [group-config]
  (some-> (config-model/localized-name group-config i18n-model/default-lang)
          str/lower-case
          (= "renewal")))

(defn config-name-matches-fn [group-config]
  (fn [other-config]
    (= (str/lower-case (config-model/localized-name group-config i18n-model/default-lang))
       (str/lower-case (config-model/localized-name other-config i18n-model/default-lang)))))

(defn renewal-workspace? [workspace]
  (-> (workspace-model/workspace-application workspace)
      :application/work-type-group-configs
      first
      renewal-group-config?))

(defn workspace-licence [workspace]
  (some->> workspace
           :licence/_workspaces
           first))

(defn original-licence-workspace [licence-ws]
  (some->> licence-ws
           workspace-licence
           :licence/workspaces
           (filter #(and (= :permit-type/L (:workspace/permit-type %))
                         (not (renewal-workspace? %))))
           first))

(defn new-number-on-renewal? [workspace]
  (config-model/resolve-municipal-case-id-value :group.config/municipal-case-id-new-number-on-renewal?
                                                :group/municipal-case-id-new-number-on-renewal?
                                                workspace))

(defn new-number-to-be-assigned? [workspace]
  (or (not (renewal-workspace? workspace))
      (new-number-on-renewal? workspace)))

(defmethod group-model/get-authority-group :entity.type/licence
  [{:domain/keys [authority-group]}]
  authority-group)

(def reference-date-values
  #{:verdict-default-config.date-input.reference-date/verdict-draft-creation
    :verdict-default-config.date-input.reference-date/linked-licence-expiration})

(def default-reference-date-value
  :verdict-default-config.date-input.reference-date/verdict-draft-creation)

(defn linked-licence-reference-date? [reference-key]
  (= reference-key :verdict-default-config.date-input.reference-date/linked-licence-expiration))
